/**
 * Custom client entry that supports two rendering modes:
 *
 * 1. **SSR hydration** — When the SSR Worker rendered the page, the server
 *    injects `window.__reactRouterContext`. The client hydrates against the
 *    server-rendered HTML using `HydratedRouter`.
 *
 * 2. **SPA fallback** — When the page is served as a static shell from R2
 *    (no SSR Worker), there is no `__reactRouterContext`. The client creates
 *    a browser router with `createBrowserRouter` / `RouterProvider` and does
 *    a full client-side render.  Route `clientLoader` functions provide data.
 */

import CssBaseline from "@mui/material/CssBaseline";
import {ThemeProvider} from "@mui/material/styles";
import {startTransition, Suspense} from "react";
import {hydrateRoot, createRoot} from "react-dom/client";
import {createBrowserRouter, RouterProvider, Outlet} from "react-router";
import {HydratedRouter} from "react-router/dom";

import Layout from "./components/Layout";
import {theme} from "./styles/theme";
import "./styles/fonts.css";

declare global {
    interface Window {
        __reactRouterContext?: unknown;
    }
}

function RootLayout() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Layout>
                <Outlet />
            </Layout>
        </ThemeProvider>
    );
}

function createSpaRouter() {
    return createBrowserRouter(
        [
            {
                path: "/",
                element: <RootLayout />,
                children: [
                    {
                        path: "sitemap",
                        lazy: async () => {
                            const mod = await import("./components/SitemapPage");
                            return {Component: mod.default};
                        },
                    },
                    {
                        path: "sitemap/:category",
                        lazy: async () => {
                            const mod = await import("./components/CategoryPage");
                            return {Component: mod.default};
                        },
                    },
                    {
                        path: "sitemap/:category/:subcategory",
                        lazy: async () => {
                            const mod = await import("./components/SubCategoryPage");
                            return {Component: mod.default};
                        },
                    },
                    {
                        path: "search",
                        lazy: async () => {
                            const mod = await import("./components/SearchPage");
                            return {Component: mod.default};
                        },
                    },
                ],
            },
        ],
        {
            basename: import.meta.env.BASE_URL.replace(/\/$/, ""),
        },
    );
}

startTransition(() => {
    if (window.__reactRouterContext) {
        // Canonical URLs (/search, /sitemap/*) reach the SSR worker via an
        // internal /site-search rewrite in frontend-proxy, so the browser URL
        // lacks the build-time basename. Hydrate without it or the router
        // matches nothing and the page never becomes interactive.
        if (!window.location.pathname.startsWith("/site-search")) {
            (window.__reactRouterContext as {basename?: string}).basename = "";
        }
        hydrateRoot(document, <HydratedRouter />);
    } else {
        const rootEl = document.getElementById("root");
        if (rootEl) {
            const router = createSpaRouter();
            createRoot(rootEl).render(
                <Suspense>
                    <RouterProvider router={router} />
                </Suspense>,
            );
        }
    }
});
